/**
 * Shared action builders for the sub-menu (PLAN §2.5). A provider composes
 * its own list from these; [0] is always the Enter default.
 */
import { ExternalLink, Copy, Link, CornerDownLeft, PanelTopOpen } from 'lucide-react';
import { copyTextToClipboard } from '../utils/clipboard';
import { useNotificationStore } from '../store/notificationStore';
import i18n from '../i18n';

/** Copy and confirm with a short notice (`done`: the translated "… copied"). Resolves true when it worked. */
export async function copyWithNotice(text, done) {
  const ok = await copyTextToClipboard(String(text ?? ''));
  const notes = useNotificationStore.getState();
  if (ok) notes.success?.(done, i18n.t('palette.notice.title'));
  else notes.error?.(i18n.t('palette.notice.copyFailed'), i18n.t('palette.notice.title'));
  return ok;
}

export const openAction = (run, extra = {}) => ({ id: 'open', titleKey: 'palette.action.open', icon: CornerDownLeft, run, ...extra });

export const openNewTabAction = (run) => ({
  id: 'openNewTab',
  titleKey: 'palette.action.openNewTab',
  icon: PanelTopOpen,
  shortcut: { mod: true, key: 'Enter' },
  run: (r, ctx) => run(r, ctx, { newTab: true }),
});

export const copyLinkAction = (linkOf) => ({
  id: 'copyLink',
  titleKey: 'palette.action.copyLink',
  icon: Link,
  shortcut: { mod: true, key: 'c' },
  run: (r) => copyWithNotice(linkOf(r), i18n.t('palette.notice.link')),
});

export const copyNameAction = () => ({
  id: 'copyName',
  titleKey: 'palette.action.copyName',
  icon: Copy,
  run: (r) => copyWithNotice(r.title, i18n.t('palette.notice.name')),
});

export const copyTextAction = (id, titleKey, textOf, whatKey) => ({
  id,
  titleKey,
  icon: Copy,
  run: (r) => copyWithNotice(textOf(r), i18n.t(whatKey)),
});

/** "Enter the scope" — keeps the palette open and sets the chip. */
export const enterScopeAction = (providerId) => ({
  id: 'enterScope',
  titleKey: 'palette.action.enterScope',
  icon: ExternalLink,
  keepOpen: true,
  run: (_r, ctx) => ctx.palette?.setScope?.(providerId),
});
